import { toast } from "react-toastify";
import currencyFormatter from "currency-formatter";

export const delay = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const getBase64 = (file: File | Blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const gcd = (a: number, b: number): number => {
  return b === 0 ? a : gcd(b, a % b);
};
export const getAspectRatio = (width: number, height: number): string => {
  const ratio = gcd(width, height);
  return `${width / ratio}:${height / ratio}`;
};

export enum UploadFileType {
  JPEG = "image/jpeg",
  PNG = "image/png",
  JPG = "image/jpg",
  MP4 = "video/mp4",
  MOV = "video/quicktime",
  PDF = "application/pdf",
  CSV = "text/csv",
  EXCEL = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  DOCX = "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
}

export const beforeUploadFile = (
  { size, type }: File | Blob,
  sizeLimitMB: number = 5,
  allowedTypes: UploadFileType[]
): boolean => {
  const isTypeAllowed: boolean =
    allowedTypes.length > 0
      ? allowedTypes.find((at) => at === type) !== undefined
      : true;
  const isSizeAllowed: boolean = size / 1024 <= 1024 * sizeLimitMB;
  if (!isSizeAllowed)
    toast.error(
      `Tamaño de archivo no permitido, el tamaño tiene que ser menor a ${sizeLimitMB} Megabytes`
    );
  if (!isTypeAllowed)
    toast.error(
      `Tipo de archivo no permitido, el tipo tiene que ser ${allowedTypes.join(
        " - "
      )}`
    );
  return isTypeAllowed && isSizeAllowed;
};

export const blobFromDataURI = (dataURI: string) => {
  const splitDataURI = dataURI.split(",");
  const byteString =
    splitDataURI[0].indexOf("base64") >= 0
      ? atob(splitDataURI[1])
      : decodeURI(splitDataURI[1]);
  const mimeString = splitDataURI[0].split(":")[1].split(";")[0];
  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
  return new Blob([ia], { type: mimeString });
};

export const urlToDataURL = async (
  url: string,
  contentType = "image/png"
): Promise<string> => {
  const blob = await fetch(url).then((r) => r.blob());
  const dataUrl: string = await new Promise((resolve: Function) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });

  return `data:${contentType};${dataUrl.split(";")[1]}`;
};

export const makeid = (length: number) => {
  let result = "";
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

/**
 * Convierte un monto en pesos chilenos tal como viene del API a número.
 * Soporta: "12345", "12.345", "12.345,50" (miles con punto, decimales con coma).
 * Si se pasa tal cual un string con puntos de miles a `currency-formatter` / `Number`,
 * "12.345" se interpreta como 12.345 en JS y el precio se muestra mal.
 */
export const parseChileMoneyString = (
  raw: string | undefined | null
): number => {
  if (raw == null) return NaN;
  const s = String(raw).trim();
  if (s === "") return NaN;
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  const lastComma = s.lastIndexOf(",");
  if (lastComma >= 0) {
    const intPart = s.slice(0, lastComma).replace(/\./g, "");
    const decPart = s.slice(lastComma + 1).replace(/\./g, "");
    const n = parseFloat(`${intPart}.${decPart}`);
    return Number.isFinite(n) ? n : NaN;
  }
  const dots = s.match(/\./g)?.length ?? 0;
  if (dots === 0) {
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : NaN;
  }
  if (dots > 1) {
    const n = parseInt(s.replace(/\./g, ""), 10);
    return Number.isFinite(n) ? n : NaN;
  }

  const [left, right] = s.split(".");
  if (!left || !right) return NaN;

  // Single dot can be decimal ("59488.00") or thousand separator ("59.488").
  const looksLikeThousands = /^\d+$/.test(left) && /^\d{3}$/.test(right);
  if (looksLikeThousands) {
    const n = parseInt(`${left}${right}`, 10);
    return Number.isFinite(n) ? n : NaN;
  }

  const n = parseFloat(s);
  return Number.isFinite(n) ? n : NaN;
};

export const CL$Format = (value: any, code?: string, symbol?: string) => {
  const precision = value - Math.floor(value) === 0 ? 0 : 2;
  const Format = currencyFormatter.format(value, {
    code: code,
    symbol,
    decimal: ",",
    thousand: ".",
    precision,
    format: {
      pos: "%s%v",
      neg: "(%s%v)",
      zero: "%s%v",
    },
  });

  return Format;
};

export const checkServerStatus = async (
  url: string,
  connectionResult?: (isOnline: boolean) => void
) => {
  const done = connectionResult ?? (() => {});
  try {
    // Do not gate on navigator.onLine — it is often wrong (VPN, captive portals, Chrome quirks).
    if (!url?.startsWith("http")) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn(
          "[checkServerStatus] Invalid REACT_APP_MAIN_URL; got status URL:",
          url
        );
      }
      return done(false);
    }
    const ctrl = new AbortController();
    const t = window.setTimeout(() => ctrl.abort(), 15000);
    const response = await fetch(url, {
      method: "GET",
      cache: "no-store",
      mode: "cors",
      signal: ctrl.signal,
    });
    window.clearTimeout(t);
    // GET: some APIs mis-handle HEAD (wrong Content-Length / early close) and break fetch in the browser.
    return done(response.ok);
  } catch {
    return done(false);
  }
};
